import React from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { useToast } from '../context/ToastContext'
import { useWebSocket } from '../context/WebSocketContext'
import { gamesApi } from '../api/games'
import { friendsApi } from '../api/friends'

export default function GameSetup() {
    const { gameId } = useParams()
    const { user } = useAuth()
    const { addToast } = useToast()
    const { lastMessage } = useWebSocket()
    const navigate = useNavigate()

    const [game, setGame] = React.useState(null)
    const [friends, setFriends] = React.useState([])
    const [inviteUsername, setInviteUsername] = React.useState('')
    const [inviting, setInviting] = React.useState(false)
    const [starting, setStarting] = React.useState(false)
    const [cancelling, setCancelling] = React.useState(false)
    const [copied, setCopied] = React.useState(false)

    function copyJoinLink() {
        const link = `${window.location.origin}/join/${game.join_code}`
        navigator.clipboard.writeText(link).then(() => {
            setCopied(true)
            setTimeout(() => setCopied(false), 2000)
        })
    }

    async function fetchGame() {
        try {
            const data = await gamesApi.getGame(gameId)
            setGame(data)
            if (data.status === 'active') navigate(`/game/${gameId}`, { replace: true })
        } catch (err) {
            addToast(err.message, 'error')
            navigate('/lobby', { replace: true })
        }
    }

    React.useEffect(() => { fetchGame() }, [gameId]) // eslint-disable-line react-hooks/exhaustive-deps

    React.useEffect(() => {
        friendsApi.listFriends()
            .then(setFriends)
            .catch(err => addToast(err.message, 'error'))
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    React.useEffect(() => {
        if (!lastMessage) return
        if (lastMessage.event === 'player_joined' || lastMessage.event === 'game_started') fetchGame()
    }, [lastMessage]) // eslint-disable-line react-hooks/exhaustive-deps

    async function handleInvite(e) {
        e.preventDefault()
        if (!inviteUsername.trim()) return
        setInviting(true)
        try {
            await gamesApi.invitePlayer(gameId, inviteUsername.trim())
            addToast(`Invitation sent to ${inviteUsername.trim()}`, 'success')
            setInviteUsername('')
            fetchGame()
        } catch (err) {
            addToast(err.message, 'error')
        } finally {
            setInviting(false)
        }
    }

    async function handleCancel() {
        setCancelling(true)
        try {
            await gamesApi.cancelGame(gameId)
            navigate('/lobby', { replace: true })
        } catch (err) {
            addToast(err.message, 'error')
            setCancelling(false)
        }
    }

    async function handleStart() {
        setStarting(true)
        try {
            await gamesApi.startGame(gameId)
            navigate(`/game/${gameId}`, { replace: true })
        } catch (err) {
            addToast(err.message, 'error')
            setStarting(false)
        }
    }

    if (!game) {
        return (
            <main className="flex min-h-screen w-full items-center justify-center bg-bankroll-navy">
                <p className="text-gray-300">Loading game…</p>
            </main>
        )
    }

    const isHost = game.created_by_id === user?.id
    const playerCount = game.players?.length ?? 0
    const canStart = isHost && playerCount >= 1
    const canInvite = isHost && playerCount < 6

    const pendingInvitations = (game.invitations ?? []).filter(i => i.status === 'pending')
    const acceptedPlayerIds  = new Set((game.players ?? []).map(p => p.player_id))
    const visibleInvitations = (game.invitations ?? []).filter(
        i => !acceptedPlayerIds.has(i.invited_user_id)
    )
    const allDeclined = isHost &&
        (game.invitations ?? []).length > 0 &&
        pendingInvitations.length === 0 &&
        playerCount === 1

    return (
        <main className="page-main">
            <p className="text-gray-400 text-[0.95rem] m-0">Invite players, or start solo.</p>

            <section>
                <h3 className="text-base font-bold text-gray-300 mb-2.5">Players ({playerCount}/6)</h3>
                <ul className="list-none m-0 p-0 flex flex-col gap-2">
                    {game.players?.map(p => (
                        <li key={p.player_id} className="flex items-center justify-between px-1 py-1.5 border-b border-gray-700 last:border-0">
                            <span className="font-bold text-gray-100">{p.username}</span>
                            {p.player_id === game.created_by_id
                                ? <span className="text-[0.75rem] font-bold bg-bankroll-primary text-white rounded-full px-2.5 py-0.5 uppercase tracking-wide">Host</span>
                                : <span className="text-[0.75rem] font-bold bg-green-700 text-white rounded-full px-2.5 py-0.5 uppercase tracking-wide">Joined</span>
                            }
                        </li>
                    ))}
                    {visibleInvitations.map(inv => (
                        <li key={inv.invited_user_id} className="flex items-center justify-between px-1 py-1.5 border-b border-gray-700 last:border-0">
                            <span className="font-bold text-gray-400">{inv.invited_username}</span>
                            {inv.status === 'pending'
                                ? <span className="text-[0.75rem] font-bold bg-yellow-600 text-white rounded-full px-2.5 py-0.5 uppercase tracking-wide">Invited</span>
                                : <span className="text-[0.75rem] font-bold bg-gray-600 text-white rounded-full px-2.5 py-0.5 uppercase tracking-wide">Declined</span>
                            }
                        </li>
                    ))}
                </ul>
                {isHost && pendingInvitations.length > 0 && (
                    <p className="text-xs text-gray-500 mt-2 m-0">
                        Waiting for {pendingInvitations.length === 1 ? '1 player' : `${pendingInvitations.length} players`} to respond…
                    </p>
                )}
            </section>

            {game.join_code && (
                <section>
                    <h3 className="text-base font-bold text-gray-300 mb-2.5">Share Link</h3>
                    <div className="flex items-center gap-2">
                        <span className="flex-1 px-3 py-2 bg-gray-800 text-gray-300 rounded-md text-sm font-mono truncate">
                            {window.location.origin}/join/{game.join_code}
                        </span>
                        <button
                            onClick={copyJoinLink}
                            className="px-4 py-2 bg-bankroll-primary text-white border-0 rounded-md font-karla font-bold text-sm cursor-pointer hover:opacity-90 whitespace-nowrap"
                        >
                            {copied ? 'Copied!' : 'Copy'}
                        </button>
                    </div>
                </section>
            )}

            {canInvite && (
                <section>
                    <h3 className="text-base font-bold text-gray-300 mb-2.5">Invite a Player</h3>
                    <form className="flex gap-2.5" onSubmit={handleInvite}>
                        <select
                            className="flex-1 px-3 py-2.5 border border-gray-300 rounded-md font-karla text-base outline-none focus:border-bankroll-primary bg-white text-bankroll-text"
                            value={inviteUsername}
                            onChange={e => setInviteUsername(e.target.value)}
                            required
                        >
                            <option value="">Select a friend…</option>
                            {friends.map(f => (
                                <option key={f.friend_id} value={f.username}>
                                    {f.first_name} {f.last_name}
                                </option>
                            ))}
                        </select>
                        <button
                            className="px-5 py-2.5 bg-bankroll-primary text-white border-0 rounded-md font-karla text-base font-bold cursor-pointer disabled:opacity-60 disabled:cursor-default"
                            type="submit"
                            disabled={inviting}
                        >
                            {inviting ? 'Sending…' : 'Invite'}
                        </button>
                    </form>
                </section>
            )}

            {isHost && (
                <section className="flex gap-3 flex-wrap">
                    {canStart && (
                        <button
                            className="px-8 py-3.5 bg-bankroll-success text-white border-0 rounded-lg font-karla text-lg font-bold cursor-pointer hover:opacity-90 disabled:opacity-60 disabled:cursor-default"
                            onClick={handleStart}
                            disabled={starting}
                        >
                            {starting ? 'Starting…' : 'Start Game'}
                        </button>
                    )}
                    {allDeclined && (
                        <button
                            className="px-8 py-3.5 bg-red-700 text-white border-0 rounded-lg font-karla text-lg font-bold cursor-pointer hover:opacity-90 disabled:opacity-60 disabled:cursor-default"
                            onClick={handleCancel}
                            disabled={cancelling}
                        >
                            {cancelling ? 'Cancelling…' : 'Cancel Game'}
                        </button>
                    )}
                </section>
            )}

            {!isHost && (
                <p className="text-gray-400 text-sm m-0">Waiting for the host to start the game…</p>
            )}
        </main>
    )
}
